import { useState } from 'react'
import PoolsEditor from './PoolsEditor'
import { inkFor } from '../../lib/ink'
import SlotMachine from './SlotMachine'
import TeamName from './TeamName'
import TeamsTable from './TeamsTable'
import type { AdminEvent } from './useAdminEvent'

/**
 * Team assignments: one team at a time, the server picks an unused mission
 * combination and saves it, then the slot machine lands on it. That team's
 * phones show the mission once it is saved.
 */
export default function AssignmentStage({ admin }: { admin: AdminEvent }) {
  const { state, busy, act } = admin
  const [spin, setSpin] = useState(0)
  const [showing, setShowing] = useState<string | null>(null)
  const [spinning, setSpinning] = useState(false)
  const [announcement, setAnnouncement] = useState('')

  if (!state) return null

  const byId = new Map(state.roster.map((p) => [p.id, p]))
  const nameOf = (id: string) => byId.get(id)?.name ?? id
  const missionOf = (teamId: string) => state.missions.find((m) => m.id === state.assignments[teamId]) ?? null

  const next = state.teams.find((t) => !state.assignments[t.id]) ?? null
  const { agencies, domains, technologies } = state.pools
  const ready = agencies.length > 0 && domains.length > 0 && technologies.length > 0
  const shownTeam = state.teams.find((t) => t.id === showing) ?? null
  const shownMission = shownTeam ? missionOf(shownTeam.id) : null
  const focusTeam = spinning || !next ? shownTeam : shownTeam ?? next

  async function spinNext() {
    if (!next) return
    const team = next
    setAnnouncement('')
    if (await act({ type: 'assignNextMission' })) {
      setShowing(team.id)
      setSpinning(true)
      setSpin((n) => n + 1)
    }
  }

  function onLanded() {
    setSpinning(false)
    if (shownTeam && shownMission) {
      setAnnouncement(
        `Team ${shownTeam.name}: ${shownMission.mission}, ${shownMission.clientDomain}, ${shownMission.technology}.`,
      )
    }
  }

  return (
    <section aria-labelledby="assignment-stage" className="flex flex-col gap-8">
      <p className="sr-only" role="status">
        {announcement}
      </p>

      <div className="flex flex-col gap-4">
        <h2 id="assignment-stage" className="wordmark-display-fluid">
          {focusTeam ? <TeamName name={focusTeam.name} color={focusTeam.color} /> : 'Team assignments'}
        </h2>
        {focusTeam && (
          <p className="text-2xl font-black">{focusTeam.memberIds.map(nameOf).join(' + ')}</p>
        )}

        <SlotMachine
          spin={spin}
          onLanded={onLanded}
          reels={[
            { label: 'Mission', kind: 'agency', values: agencies, target: shownMission?.mission ?? '?' },
            { label: 'Domain', kind: 'domain', values: domains, target: shownMission?.clientDomain ?? '?' },
            { label: 'Technology', kind: 'technology', values: technologies, target: shownMission?.technology ?? '?' },
          ]}
        />

        {next ? (
          <div className="flex flex-wrap items-center gap-4">
            <button
              type="button"
              className="cursor-pointer rounded-lg border-4 border-[#0a2240] px-6 py-3 text-lg font-black shadow-md hover:brightness-110 disabled:cursor-default disabled:opacity-50"
              style={{ background: next.color, color: inkFor(next.color) }}
              disabled={busy || spinning || !ready}
              onClick={spinNext}
            >
              {spinning ? 'Spinning' : `Spin the assignment for Team ${next.name}`}
            </button>
            {!ready && (
              <p className="font-semibold text-[var(--page-danger)]">Add at least one mission, domain, and technology below, then save.</p>
            )}
          </div>
        ) : (
          <p className="text-2xl font-black text-[var(--page-success)]">Every team has its assignment. Go build.</p>
        )}
      </div>

      <TeamsTable state={state} />

      <details open={!ready} className="rounded-lg border border-[var(--page-rule)] p-5">
        <summary className="cursor-pointer text-lg font-black">
          Reel lists ({agencies.length} missions, {domains.length} domains, {technologies.length} technologies)
        </summary>
        <p className="mt-2 mb-4 text-sm text-[var(--page-ink-muted)]">Each reel draws from its own list.</p>
        <PoolsEditor admin={admin} />
      </details>
    </section>
  )
}
